import React, { useState } from 'react';
import {
  Modal,
  Form,
  message,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Descriptions,
  Divider,
  List,
  Tag,
  Radio,
} from 'antd';
import moment from 'moment';
import _axios from '../../services/api';
import { API_ROUTES } from '../../utils/constants';
import { getAuthToken } from '../../utils/authHelpers';
import { useDispatch, useSelector } from 'react-redux';
import {
  get_all_Subscriptions_jobseeker,
  get_latest_Subscriptions_jobseeker,
} from '../../features/UserInfo/UserSlice';
import { useNavigate } from 'react-router-dom';
import './JobSeekerSubscription.scss';

const { Title, Text } = Typography;

const candidatePlans = [
  {
    name: 'Standard',
    price: { monthly: '$19.99', annually: '$199.99' },
    storage: '200 GB Storage',
    users: '1 User Allowed',
    bandwidth: 'Send up to 1 GB',
    email: '10 Email Accounts',
    highlight: false,
  },
  {
    name: 'Professional',
    price: { monthly: '$29.99', annually: '$249.99' },
    storage: '500 GB Storage',
    users: '2 Users Allowed',
    bandwidth: 'Send up to 5 GB',
    email: '25 Email Accounts',
    highlight: true,
  },
  {
    name: 'Master',
    price: { monthly: '$49.99', annually: '$399.99' },
    storage: '1 TB Storage',
    users: '5 Users Allowed',
    bandwidth: 'Send up to 10 GB',
    email: 'Unlimited Email Accounts',
    highlight: false,
  },
];

const JobSeekerSubscription = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState('monthly');
  const [currency, setCurrency] = useState('INR');
  const [form] = Form.useForm();
  const token = getAuthToken();
  const [selectedPlan, setSelectedPlan] = useState(null);

  const { userProfile } = useSelector((s) => s.global);
  const { allSubscriptions, latestSubscriptions } = useSelector(
    (s) => s.userData
  );

  const handleSubmit = async () => {
    if (!selectedPlan) return message.warning('Select a plan first');
    setLoading(true);
    try {
      const numeric = Number(
        (currency === 'USD'
          ? selectedPlan.price[billing]
          : selectedPlan.price[billing].replace('$', '₹')
        ).replace(/[^0-9.]/g, '')
      );
      const res = await _axios.post(
        API_ROUTES.JOBSEEKER.GET_ALL_SUBSCRIPTIONS,
        {
          referalCode: 'values.referalCode',
          jobSeekerId: userProfile?._id,
          amount: numeric,
          currency,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        message.success('Subscription added successfully');
        dispatch(get_latest_Subscriptions_jobseeker(navigate));
        dispatch(get_all_Subscriptions_jobseeker(navigate));
        form.resetFields();
        onClose();
      } else {
        message.error(res.data.error || 'Something went wrong');
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      centered
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      className="jobseekerSubscription"
    >
      <Title level={3} className="text-center text-white large-title">
        {userProfile?.name} — Choose Your Subscription Plan
      </Title>

      {/* ── Currency Toggle ── */}
        <Row justify="center" gutter={[16,16]} style={{ marginBottom: 24 }}>
      <Col>
        <Radio.Group
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          optionType="button"
          buttonStyle="solid"
          size="middle"
        >
          <Radio.Button  className={currency === 'INR' ? 'blueGradiant border-none' : 'border-none'} value="INR">₹ INR</Radio.Button>
          <Radio.Button  className={currency === 'USD' ? 'blueGradiant border-none' : 'border-none'} value="USD">$ USD</Radio.Button>
        </Radio.Group>
      </Col>

      {/* ── Billing Toggle ── */}
      <Col>
        <Radio.Group
          value={billing}
          onChange={(e) => setBilling(e.target.value)}
          optionType="button"
          buttonStyle="solid"
          size="middle"
        >
           <Radio.Button
            className={billing === 'monthly' ? 'blueGradiant border-none' : 'border-none'}
            value="monthly"
          >
            Monthly
          </Radio.Button>
          <Radio.Button
            className={billing === 'annually' ? 'blueGradiant border-none' : 'border-none'}
            value="annually"
          >
            Annually
          </Radio.Button>
         
        </Radio.Group>
      </Col>
      </Row>

      {/* ── Plan Cards ── */}
      <Row gutter={[16, 16]} className="plans-row">
        {candidatePlans.map((plan) => (
          <Col
            key={plan.name}
            xs={24}
            sm={12}
            md={8}
            onClick={() => setSelectedPlan(plan)}
          >
            <Card
              className={
                selectedPlan?.name === plan.name
                  ? 'active-plan-card mb40'
                  : `plan-card mb40 ${plan.highlight ? 'highlight-card' : ''}`
              }
              hoverable
            >
              <Card.Meta
                title={
                  <Tag className="text-white roundTags text-black ">
                    {plan.name}
                  </Tag>
                }
                description={
                  <h2 className="text-white">
                    {currency === 'USD'
                      ? plan.price[billing]
                      : plan.price[billing].replace('$', '₹')}
                  </h2>
                }
              />
              <ul className="plan-features">
                <li className="text-white">{plan.storage}</li>
                <li className="text-white">{plan.users}</li>
                <li className="text-white">{plan.bandwidth}</li>
                <li className="text-white">{plan.email}</li>
              </ul>
            </Card>
          </Col>
        ))}
      </Row>

      {latestSubscriptions?.name && (
        <>
          <Title level={5}>Current Subscription</Title>
          <Card className="green-box-shadow" style={{ marginBottom: 24 }}>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Type">
                <Tag color={latestSubscriptions.type === 'premium' ? 'gold' : 'blue'}>
                  {latestSubscriptions.type?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={latestSubscriptions.status === 'active' ? 'green' : 'red'}>
                  {latestSubscriptions.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Start Date">
                {moment(latestSubscriptions.startDateTime).format('DD MMM YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Expiry Date">
                {moment(latestSubscriptions.expirationDateTime).format('DD MMM YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Auto Renew" span={2}>
                {latestSubscriptions.renewalDetails?.isAutoRenew ? 'Yes' : 'No'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </>
      )}

      <Row justify="end" gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Button
            type="primary"
            htmlType="button"
            shape="round"
            className="bg-white fullwidth btnfonts text-black"
            loading={loading}
            // onClick={handleSubmit}
          >
            Subscribe
          </Button>
        </Col>
      </Row>

      {Array.isArray(allSubscriptions?.data) && allSubscriptions.data.length > 0 && (
        <>
          <Divider />
          <Title level={5}>Subscription History</Title>
          <List
            itemLayout="vertical"
            dataSource={allSubscriptions.data}
            renderItem={(item) => (
              <Card key={item._id} style={{ marginBottom: 16 }} bordered>
                <Row justify="space-between" align="middle">
                  <Text strong>
                    {item.name} ({item.type?.toUpperCase()})
                  </Text>
                  <Tag color={item.status === 'active' ? 'green' : 'red'}>
                    {item.status}
                  </Tag>
                </Row>
                <Text type="secondary">
                  {moment(item.startDateTime).format('DD MMM YYYY')} →{' '}
                  {moment(item.expirationDateTime).format('DD MMM YYYY')}
                </Text>
              </Card>
            )}
          />
        </>
      )}
    </Modal>
  );
};

export default JobSeekerSubscription;
